import { Component, ErrorInfo, ReactNode } from 'react'
import { ShieldAlert, RefreshCw, Home } from 'lucide-react'

interface Props {
    children: ReactNode
}

interface State {
    hasError: boolean
    error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
    }

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo)
    }

    public render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-background px-4">
                    <div className="text-center max-w-md">
                        <div className="flex justify-center mb-6">
                            <div className="h-20 w-20 rounded-full bg-red-50 dark:bg-red-950/30 flex items-center justify-center">
                                <ShieldAlert className="h-10 w-10 text-red-500" />
                            </div>
                        </div>
                        <h1 className="text-2xl font-bold text-foreground mb-2">
                            Oops! Something went wrong
                        </h1>
                        <p className="text-muted-foreground mb-6">
                            This one's on us — the application ran into an unexpected problem. Your data is safe.
                        </p>
                        <div className="flex items-center justify-center gap-3">
                            <button
                                onClick={() => window.location.reload()}
                                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm font-medium transition-colors"
                            >
                                <RefreshCw className="h-4 w-4" />
                                Refresh Page
                            </button>
                            <button
                                onClick={() => { window.location.href = '/' }}
                                className="inline-flex items-center gap-2 px-4 py-2.5 border rounded-lg hover:bg-muted text-sm font-medium transition-colors"
                            >
                                <Home className="h-4 w-4" />
                                Go to Dashboard
                            </button>
                        </div>
                        {this.state.error && (
                            <details className="mt-6 text-left">
                                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground transition-colors">
                                    Technical details
                                </summary>
                                <pre className="mt-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 p-3 rounded-lg overflow-auto max-h-32 border border-red-200 dark:border-red-900">
                                    {this.state.error.message}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}
